import type { JSX } from 'react';
import { Field } from '@/app/ui/Field';
import { controlStyles as c, Segment, cx } from '@/app/ui/controls';
import { FONT_FAMILIES, cssFontStack, getFamily } from '@/catalogs/fonts';
import { getTemplate, templateUsesSlot } from '@/catalogs/templates';
import type { Project, TextAlign } from '@/core/project/types';
import type { FontState } from '../hooks';
import type { EditorAction } from '../state';
import styles from './panels.module.css';

export type TextPanelProps = {
  project: Project;
  fontState: FontState;
  dispatch: (action: EditorAction) => void;
};

const SAMPLE = 'Съешь ещё этих мягких французских булок';

export function TextPanel({ project, fontState, dispatch }: TextPanelProps): JSX.Element {
  const template = getTemplate(project.template.id);
  const family = getFamily(project.caption.fontId);
  const usesTitle = templateUsesSlot(template, 'title');
  const usesCaption = templateUsesSlot(template, 'caption');
  const usesNote = templateUsesSlot(template, 'note');

  const unusedHint = (slot: string): string =>
    `Выбранный шаблон «${template.name}» не показывает ${slot}. Текст сохранится в проекте.`;

  return (
    <div className={styles.panel}>
      <p className={styles.sizeNote}>
        Подпись — это видимый текст на карточке. Она не попадает в QR и не меняет закодированную
        строку.
      </p>

      <Field label="Заголовок" hint={usesTitle ? undefined : unusedHint('заголовок')}>
        {({ id, describedBy }) => (
          <input
            id={id}
            aria-describedby={describedBy}
            className={cx(c.input, !usesTitle && c.invalid && undefined)}
            type="text"
            maxLength={300}
            value={project.title}
            placeholder="Например: Осенний каталог"
            onChange={(e) => dispatch({ type: 'set-title', value: e.target.value })}
          />
        )}
      </Field>

      <Field label="Подпись" hint={usesCaption ? undefined : unusedHint('подпись')}>
        {({ id, describedBy }) => (
          <input
            id={id}
            aria-describedby={describedBy}
            className={c.input}
            type="text"
            maxLength={300}
            value={project.caption.text}
            placeholder="Например: Наведите камеру телефона"
            onChange={(e) => dispatch({ type: 'set-caption', value: e.target.value })}
          />
        )}
      </Field>

      <Field label="Пояснение" hint={usesNote ? undefined : unusedHint('пояснение')}>
        {({ id, describedBy }) => (
          <textarea
            id={id}
            aria-describedby={describedBy}
            className={c.textarea}
            maxLength={500}
            value={project.note}
            placeholder="Мелкий текст под кодом"
            onChange={(e) => dispatch({ type: 'set-note', value: e.target.value })}
          />
        )}
      </Field>

      <Field
        label="Шрифт"
        hint={`${family.hint}. Лицензия ${family.license}, файл поставляется вместе с приложением.`}
      >
        {({ id, describedBy }) => (
          <select
            id={id}
            aria-describedby={describedBy}
            className={c.select}
            value={project.caption.fontId}
            onChange={(e) => dispatch({ type: 'set-font', fontId: e.target.value })}
          >
            {FONT_FAMILIES.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        )}
      </Field>

      <Field label="Начертание" hint="Только реальные начертания из файла: bold не имитируется.">
        {() => (
          <Segment
            name="weight"
            ariaLabel="Начертание"
            value={String(project.caption.weight)}
            options={family.faces.map((face) => ({
              value: String(face.weight),
              label: `${face.label} · ${face.weight}`,
            }))}
            onChange={(value) => dispatch({ type: 'set-weight', weight: Number(value) })}
          />
        )}
      </Field>

      <p
        className={styles.fontPreview}
        style={{ fontFamily: cssFontStack(family.id), fontWeight: project.caption.weight }}
      >
        {SAMPLE}
      </p>
      {fontState.status === 'loading' ? (
        <p className={styles.sizeNote}>Загружается файл шрифта…</p>
      ) : null}
      {fontState.status === 'error' ? (
        <p className={styles.errorBox} role="alert">
          {fontState.message} Подпись не будет добавлена в файл, пока шрифт не загрузится.
        </p>
      ) : null}

      <div className={styles.two}>
        <Field
          label={`Кегль подписи: ${project.caption.size} px`}
          hint="Размер указан в пикселях итогового документа."
        >
          {({ id, describedBy }) => (
            <input
              id={id}
              aria-describedby={describedBy}
              className={c.slider}
              type="range"
              min={8}
              max={Math.max(24, Math.round(project.output.widthPx * 0.12))}
              step={1}
              value={project.caption.size}
              onChange={(e) => dispatch({ type: 'set-caption-size', size: Number(e.target.value) })}
            />
          )}
        </Field>

        <Field label="Выравнивание">
          {() => (
            <Segment<TextAlign>
              name="align"
              ariaLabel="Выравнивание текста"
              value={project.caption.align}
              options={[
                { value: 'left', label: 'По левому' },
                { value: 'center', label: 'По центру' },
                { value: 'right', label: 'По правому' },
              ]}
              onChange={(align) => dispatch({ type: 'set-align', align })}
            />
          )}
        </Field>
      </div>
    </div>
  );
}
